/**
 * 实时帧的**事件总线**——帧在进程内的广播线。
 *
 * ## 为什么在 shared
 *
 * 生产者（`features/events/useEventInvalidations.ts` 收到 SSE 帧）与消费者
 * （`features/events/SseToasts.tsx` 弹提示、**`features/notifications/catchUp.ts`
 * 补弹**）**分属两个 feature** ⇒ 共用的东西住 `shared/`，不是互相 import
 * （前端分层闸 `src/test/featureLayering.test.ts`）。
 *
 * ⚠️ 它**不是**跨 feature 的业务耦合：总线是**传输层**的一根线——
 * 谁产生帧、谁消费帧，**两边都不知道对方是谁**。
 *
 * ## 语义
 *
 * - **同步派发**：`publishEnvelope` 把信封交给**当前已注册的**监听器；
 *   派发期间注册的监听器**收不到这一条**（先到先得，不排队）。
 * - **监听器抛异常不影响别人**：一个监听器坏了，其余监听器照常收到
 *   （提示链不能因为一个坏消费者而整体停摆）。
 * - **无缓冲、无重放**：总线上只有「此刻在线的监听器」。
 *   ⇒ 需要「补」的语义**不在这里**（见 `features/notifications/catchUp.ts`：
 *   补弹走的是**行**，不是总线）。
 */

/** 帧信封——`type` 是帧名，其余是扁平载荷。 */
export interface RealtimeEnvelope {
  type: string;
  [key: string]: unknown;
}

/** 监听器——收到一个信封。 */
export type RealtimeListener = (envelope: RealtimeEnvelope) => void;

const listeners = new Set<RealtimeListener>();

/** 派发一个信封给所有已注册的监听器。 */
export function publishEnvelope(envelope: RealtimeEnvelope): void {
  for (const listener of listeners) {
    try {
      listener(envelope);
    } catch {
      // 一个监听器抛异常不该拖垮其余监听器（提示链不能因为一个坏消费者而停摆）。
    }
  }
}

/** 订阅总线——返回**退订函数**。 */
export function subscribeEnvelope(listener: RealtimeListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** 清空监听器（测试用：避免跨用例串味）。 */
export function resetFrameBus(): void {
  listeners.clear();
}
